// src/utils/xp.ts

/**
 * Roddo XP + Leveling System (backend-aligned nonlinear curve)
 *
 * Display math only.
 * Backend remains the source of truth for:
 * - xp
 * - level
 * - achievement unlocks
 * - challenge completion
 *
 * Launch balance note:
 * Catch logging XP should reward useful fishing memory, not raw fish spam.
 * The helper exports at the bottom are safe to share with Cloud Functions, but
 * importing this file in the client does NOT award XP by itself.
 */

const LEVEL_THRESHOLDS = [
  0,     // lvl 1
  100,   // lvl 2
  280,   // lvl 3
  540,   // lvl 4
  900,   // lvl 5
  1400,  // lvl 6
  2100,  // lvl 7
  3050,  // lvl 8
  4300,  // lvl 9
  5900,  // lvl 10
];

const MAX_LEVEL = 100;

/** XP required to reach the START of a given level */
export function xpForLevel(level: number): number {
  const safeLevel = Math.max(1, Math.min(MAX_LEVEL, Math.floor(Number(level) || 1)));

  if (safeLevel <= LEVEL_THRESHOLDS.length) {
    return LEVEL_THRESHOLDS[safeLevel - 1];
  }

  // Backend-aligned long-term curve.
  // Level 100 lands around 208k total XP instead of the old unreachable compounding curve.
  const levelsPastTen = safeLevel - LEVEL_THRESHOLDS.length;
  const levelTenXp = LEVEL_THRESHOLDS[LEVEL_THRESHOLDS.length - 1];

  return Math.round(levelTenXp + 900 * levelsPastTen + 15 * levelsPastTen * levelsPastTen);
}

/** Determine current level from total XP */
export function levelForXP(xp: number): number {
  const safeXP = Number(xp);
  if (!Number.isFinite(safeXP) || safeXP <= 0) return 1;

  let level = 1;
  while (level < MAX_LEVEL && safeXP >= xpForLevel(level + 1)) level++;
  return level;
}

export function getAnglerRank(level: number): string {
  const lv = Math.max(1, Math.min(MAX_LEVEL, Math.floor(Number(level) || 1)));

  if (lv >= 100) return 'Roddo Legend';
  if (lv >= 90) return 'Mythic Angler';
  if (lv >= 75) return 'Master Angler';
  if (lv >= 60) return 'Veteran Angler';
  if (lv >= 45) return 'Seasoned Angler';
  if (lv >= 30) return 'Trusted Angler';
  if (lv >= 20) return 'Proven Angler';
  if (lv >= 10) return 'Rising Angler';
  if (lv >= 4) return 'New Water Scout';
  return 'New Angler';
}

export function getLevelBandLabel(level: number): string {
  const lv = Math.max(1, Math.min(MAX_LEVEL, Math.floor(Number(level) || 1)));

  if (lv >= 100) return 'Long-term Roddo mastery across catches, waters, and patterns';
  if (lv >= 90) return 'Mythic breadth built through sustained time on the water';
  if (lv >= 75) return 'Deep multi-season mastery and pattern recognition';
  if (lv >= 60) return 'Veteran record across waters, species, and conditions';
  if (lv >= 45) return 'Seasoned consistency with broad fishing history';
  if (lv >= 30) return 'Trusted pattern-building across multiple waters';
  if (lv >= 20) return 'Proven experience with growing range and consistency';
  if (lv >= 10) return 'Clear momentum and repeat time on the water';
  if (lv >= 4) return 'Early exploration and logging rhythm';
  return 'Early angler progression';
}

/**
 * XP progress within the current level
 * Keeps backward-compat keys:
 * - `progress` === `gained`
 */
export function xpProgress(xp: number) {
  const safeXP = Number(xp);
  const totalXP = Number.isFinite(safeXP) && safeXP > 0 ? safeXP : 0;

  const level = levelForXP(totalXP);
  const currentLevelXP = xpForLevel(level);
  const nextLevelXP = xpForLevel(level + 1);

  const gained = Math.max(0, totalXP - currentLevelXP);
  const needed = Math.max(1, nextLevelXP - currentLevelXP);
  const remaining = Math.max(0, nextLevelXP - totalXP);
  const percent = Math.max(0, Math.min(100, (gained / needed) * 100));

  return {
    level,
    totalXP,
    currentLevelXP,
    nextLevelXP,
    gained,
    needed,
    remaining,
    percent,
    progress: gained,
    label: `Lv ${level}`,
    rank: getAnglerRank(level),
    bandLabel: getLevelBandLabel(level),
  };
}

// -----------------------------------------------------------------------------
// Launch-safe catch XP balance helpers
// -----------------------------------------------------------------------------

export interface CatchXPInput {
  /** 1-based count of accepted catch logs for this user on the local fishing day. */
  dailyCatchIndex?: number;
  hasPhoto?: boolean;
  hasSpecies?: boolean;
  hasWeight?: boolean;
  hasLength?: boolean;
  hasGear?: boolean;
  hasWaterOrLocation?: boolean;
  hasWeather?: boolean;
  hasTimeContext?: boolean;
  isNewSpecies?: boolean;
  isNewWater?: boolean;
  isPersonalBest?: boolean;
  sharedToCommunity?: boolean;
}

export interface CatchXPBreakdown {
  baseXP: number;
  qualityXP: number;
  milestoneXP: number;
  totalXP: number;
  dailyMultiplier: number;
  label: string;
}

/**
 * Diminishing returns for same-day catch quantity.
 * 1-3 catches: normal logging day.
 * 4-5 catches: still useful, but softer.
 * 6-10 catches: hot bite / stocked water territory.
 * 11+ catches: token XP only unless quality/milestones add value.
 */
export function getDailyCatchXPMultiplier(dailyCatchIndex?: number): number {
  const n = Math.max(1, Math.floor(Number(dailyCatchIndex) || 1));
  if (n <= 3) return 1;
  if (n <= 5) return 0.65;
  if (n <= 10) return 0.35;
  if (n <= 25) return 0.15;
  return 0.08;
}

export function calculateCatchLogXP(input: CatchXPInput = {}): CatchXPBreakdown {
  const dailyMultiplier = getDailyCatchXPMultiplier(input.dailyCatchIndex);

  const scaleRepeatable = (amount: number) => {
    const base = Math.max(0, Number(amount) || 0);
    if (base <= 0) return 0;
    const scaled = Math.round(base * dailyMultiplier);
    return dailyMultiplier >= 0.15 ? Math.max(1, scaled) : scaled;
  };

  // Mirror the backend's repeatable catch awards. This is display-only and can
  // still understate a real award because server-side first-region, technique,
  // condition-family, habitat, consistency, and trophy bonuses are contextual.
  const baseXP = scaleRepeatable(4);

  let qualityXP = 0;
  if (input.hasPhoto) qualityXP += scaleRepeatable(2);
  if (input.hasWeight) qualityXP += scaleRepeatable(2);
  if (input.hasLength) qualityXP += scaleRepeatable(2);
  if (input.hasGear) qualityXP += scaleRepeatable(2);
  if (input.hasWaterOrLocation) qualityXP += scaleRepeatable(1);
  if (input.hasWeather || input.hasTimeContext) qualityXP += scaleRepeatable(1);
  if (input.sharedToCommunity) qualityXP += scaleRepeatable(4);

  let milestoneXP = 0;
  if (input.isNewSpecies) milestoneXP += 14;
  if (input.isNewWater) milestoneXP += 10;

  const totalXP = Math.max(0, Math.round(baseXP + qualityXP + milestoneXP));

  return {
    baseXP,
    qualityXP,
    milestoneXP,
    totalXP,
    dailyMultiplier,
    label: dailyMultiplier >= 1
      ? 'Backend-aligned catch XP preview'
      : 'Backend-aligned diminished catch XP preview',
  };
}

/** Flat server-authoritative XP granted once for a completed tournament. */
export const TOURNAMENT_COMPLETION_XP = 25;
